import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import {
  Input,
  Button,
  Typography,
  Segmented,
  Form,
  Spin,
  message,
} from "antd";
import { PlusOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import debounce from "lodash/debounce";
import { getRentCollectionList } from "../api/rent-collection";

const { Title, Text } = Typography;

const PAGE_SIZE = 20;
const MIN_ROOM_LEN = 2; // 房号至少输入几位才触发查询
const DEFAULT_FILTERS = { roomNumber: "", contractStatus: "focus" };

const normalize = (v = {}) => ({
  roomNumber: v.roomNumber?.trim() || undefined,
  contractStatus: v.contractStatus || "focus",
});
const keyOf = (r) => r.contractId ?? r._id;
const mergeUnique = (prev, next) => {
  const seen = new Set(prev.map(keyOf));
  return prev.concat(next.filter((r) => !seen.has(keyOf(r))));
};
const money = (v) =>
  Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 2 });

// 色调：红/橙/绿留给账单日和支付状态，周期只用蓝/紫/青/灰
const TONE = {
  red: { color: "#cf1322", bg: "rgba(255,77,79,0.12)" },
  amber: { color: "#d46b08", bg: "rgba(250,140,22,0.14)" },
  green: { color: "#389e0d", bg: "rgba(82,196,26,0.12)" },
  slate: { color: "#475569", bg: "rgba(100,116,139,0.12)" },
  blue: { color: "#0958d9", bg: "rgba(22,119,255,0.12)" },
  purple: { color: "#531dab", bg: "rgba(114,46,209,0.12)" },
  teal: { color: "#08979c", bg: "rgba(19,194,194,0.14)" },
};
const CYCLE_CONFIG = {
  daily: { label: "日付", ...TONE.slate },
  monthly: { label: "月付", ...TONE.blue },
  quarterly: { label: "季付", ...TONE.purple },
  yearly: { label: "年付", ...TONE.teal },
};

// 按自然日比较，今天到期不算逾期
const getDueInfo = (date) => {
  if (!date) return null;
  const d = dayjs(date);
  const days = d.startOf("day").diff(dayjs().startOf("day"), "day");
  const text = d.format(d.year() === dayjs().year() ? "MM-DD" : "YYYY-MM-DD");
  if (days < 0) return { ...TONE.red, text: `${text} 逾期${-days}天` };
  if (days === 0) return { ...TONE.amber, text: `${text} 今天到期` };
  if (days <= 7) return { ...TONE.amber, text: `${text} 还剩${days}天` };
  return { ...TONE.slate, text };
};

const Chip = ({ color, bg, label, children }) => (
  <span className="rc-chip" style={{ color, background: bg }}>
    {label && <span className="rc-chip-label">{label}</span>}
    {children}
  </span>
);

const RentCard = React.memo(function RentCard({ record, onClick }) {
  const cycle = CYCLE_CONFIG[record.rentPaymentCycle] || {
    label: record.rentPaymentCycle || "—",
    ...TONE.slate,
  };
  const due = getDueInfo(record.earliestDueDate);
  const paid = Number(record.totalPaidAmount || 0);
  const totalAmt = Number(record.totalAmount || 0);
  const payTone =
    paid <= 0 ? TONE.red : paid >= totalAmt ? TONE.green : TONE.amber;

  return (
    <div
      className="rc-card"
      role="button"
      tabIndex={0}
      onClick={() => onClick(record)}
      onKeyDown={(e) => e.key === "Enter" && onClick(record)}
    >
      <div className="rc-row">
        <div className="rc-left">
          <Text ellipsis strong className="rc-room">
            {record.roomNumber || "—"}
          </Text>
          <Chip color={cycle.color} bg={cycle.bg}>
            {cycle.label}
          </Chip>
        </div>
        <Chip label="押金" {...TONE.slate}>
          {record.securityDepositPayed != null
            ? `¥${money(record.securityDepositPayed)}`
            : "—"}
        </Chip>
      </div>
      <div className="rc-row rc-row-bottom">
        <div className="rc-left">
          <Chip {...(due || TONE.slate)}>{due ? due.text : "—"}</Chip>
          <Chip label="账单" {...TONE.slate}>
            {record.billCount ?? "—"}
          </Chip>
        </div>
        <Chip {...payTone}>
          ¥{money(paid)} / ¥{money(totalAmt)}
        </Chip>
      </div>
    </div>
  );
});

// 找最近的可滚动祖先，找不到就是 window
function getScrollParent(el) {
  let p = el?.parentElement;
  while (p && p !== document.body && p !== document.documentElement) {
    if (/(auto|scroll|overlay)/.test(getComputedStyle(p).overflowY)) return p;
    p = p.parentElement;
  }
  return window;
}

// 顶部栏显隐直接操作 classList，不走 state，滚动时列表不会重渲染
function useHeaderScroll(headerRef) {
  const scrollerRef = useRef(window);
  useEffect(() => {
    const header = headerRef.current;
    const scroller = getScrollParent(header);
    scrollerRef.current = scroller;
    const isWin = scroller === window;
    const read = () => {
      const y = isWin ? window.scrollY : scroller.scrollTop;
      const max = isWin
        ? document.documentElement.scrollHeight - window.innerHeight
        : scroller.scrollHeight - scroller.clientHeight;
      return Math.min(Math.max(y, 0), Math.max(max, 0)); // 夹掉 iOS 回弹
    };

    let acc = 0,
      lastY = read(),
      ticking = false;
    let touching = false,
      tx = 0,
      ty = 0,
      startY = 0,
      onInput = false;

    const setHidden = (h) => header.classList.toggle("is-hidden", h);
    const setScrolled = (y) => header.classList.toggle("is-scrolled", y > 2);
    // d > 0 表示内容向下滚（手指上滑）
    const applyDelta = (d, y) => {
      if (!d) return;
      acc = d * acc < 0 ? d : acc + d; // 换方向重新累计
      if (y <= 0 || acc < -4)
        setHidden(false); // 显示更灵敏
      else if (y > 56 && acc > 8) setHidden(true);
    };
    const blurInput = () => {
      const a = document.activeElement;
      if (a?.tagName === "INPUT" && header.contains(a)) a.blur();
    };

    // 兜底：滚轮 / 键盘 / 惯性滚动
    const update = () => {
      ticking = false;
      const y = read();
      const dy = y - lastY;
      lastY = y;
      setScrolled(y);
      if (!touching) applyDelta(dy, y); // 触摸中由 touchmove 负责，避免重复累计
    };
    const onScroll = () => {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(update);
      }
    };

    // 主路径：触摸，手指一动立即响应
    const onTouchStart = (e) => {
      const t = e.touches[0];
      touching = true;
      tx = t.clientX;
      ty = startY = t.clientY;
      onInput = !!e.target.closest?.("input"); // 在输入框里拖动光标不算滚动
    };
    const onTouchMove = (e) => {
      const t = e.touches[0];
      const dy = t.clientY - ty; // 手指向下为正
      const dx = t.clientX - tx;
      tx = t.clientX;
      ty = t.clientY;
      if (Math.abs(dy) <= Math.abs(dx)) return; // 横向手势忽略
      if (!onInput && Math.abs(t.clientY - startY) > 6) blurInput();
      const y = read();
      applyDelta(-dy, y);
      setScrolled(y);
    };
    const onTouchEnd = () => {
      touching = false;
      lastY = read();
    };
    const onWheel = (e) => {
      if (e.deltaY) blurInput();
    };

    const opt = { passive: true };
    scroller.addEventListener("scroll", onScroll, opt);
    document.addEventListener("touchstart", onTouchStart, opt);
    document.addEventListener("touchmove", onTouchMove, opt);
    document.addEventListener("touchend", onTouchEnd, opt);
    document.addEventListener("touchcancel", onTouchEnd, opt);
    document.addEventListener("wheel", onWheel, opt);
    return () => {
      scroller.removeEventListener("scroll", onScroll);
      document.removeEventListener("touchstart", onTouchStart);
      document.removeEventListener("touchmove", onTouchMove);
      document.removeEventListener("touchend", onTouchEnd);
      document.removeEventListener("touchcancel", onTouchEnd);
      document.removeEventListener("wheel", onWheel);
    };
  }, [headerRef]);

  return useCallback(() => scrollerRef.current.scrollTo({ top: 0 }), []);
}

const CSS = `
.rc-root { min-height: 100%; background: #f5f7fa; }
.rc-header {
  position: sticky; top: 0; z-index: 20;
  background: rgba(255,255,255,.94);
  border-bottom: 1px solid #e8ecf1;
  transition: transform .25s ease, box-shadow .25s ease, border-color .25s;
  will-change: transform;
}
@supports ((backdrop-filter: blur(1px)) or (-webkit-backdrop-filter: blur(1px))) {
  .rc-header {
    background: rgba(255,255,255,.72);
    -webkit-backdrop-filter: blur(14px) saturate(160%);
    backdrop-filter: blur(14px) saturate(160%);
  }
}
.rc-header.is-scrolled { border-bottom-color: transparent; box-shadow: 0 4px 14px rgba(15,23,42,.10); }
.rc-header.is-hidden { transform: translateY(-100%); box-shadow: none; }
.rc-inner { max-width: 1440px; margin: 0 auto; padding: 10px 12px; padding-top: calc(10px + env(safe-area-inset-top)); }
.rc-title-row { display: flex; align-items: center; justify-content: space-between; margin-bottom: 8px; }
.rc-filter-row { display: flex; align-items: center; gap: 8px; }
.rc-filter-row .rc-search { flex: 1; min-width: 0; }
.rc-filter-row .ant-segmented { flex-shrink: 0; }
@media (min-width: 768px) { .rc-filter-row .rc-search { flex: 0 1 320px; } }

.rc-content { max-width: 1440px; margin: 0 auto; padding: 12px 12px calc(24px + env(safe-area-inset-bottom)); transition: opacity .2s; }
.rc-grid { display: grid; gap: 10px; grid-template-columns: repeat(auto-fill, minmax(min(100%, 340px), 1fr)); }
.rc-card {
  background: #fff; border: 1px solid #f0f0f0; border-radius: 12px; padding: 14px 14px 12px;
  cursor: pointer; -webkit-tap-highlight-color: transparent; transition: background .12s, border-color .12s;
  
}
.rc-card:active { background: #f4f7fb; }
@media (hover: hover) { .rc-card:hover { border-color: #adc6ff; } }
.rc-row { display: flex; align-items: center; justify-content: space-between; gap: 8px; min-width: 0; }
.rc-row-bottom { margin-top: 9px; padding-top: 9px; border-top: 1px solid #f1f5f9; }
.rc-left { display: flex; align-items: center; gap: 7px; min-width: 0; }
.rc-room { min-width: 0; font-size: 16px; color: #1f2329; }
.rc-chip { flex-shrink: 0; padding: 2px 7px; border-radius: 5px; font-size: 12px; line-height: 18px; white-space: nowrap; }
.rc-chip-label { opacity: .75; margin-right: 3px; }
.rc-footer { min-height: 48px; display: flex; align-items: center; justify-content: center; padding: 8px 0; }
`;
export default function RentCollectionList({
  onEnterDetail,
  filters,
  showContractModal,
}) {
  const [form] = Form.useForm();
  const headerRef = useRef(null);
  const sentinelRef = useRef(null);
  const scrollToTop = useHeaderScroll(headerRef);

  useEffect(() => {
    scrollToTop(); // 每次进入列表页（含从详情返回）都回到顶部
  }, [scrollToTop]);

  // 只取首次传入的 filters（从详情返回时用来还原筛选）
  const [initial] = useState(() => ({
    roomNumber: filters?.roomNumber ?? DEFAULT_FILTERS.roomNumber,
    contractStatus: filters?.contractStatus || DEFAULT_FILTERS.contractStatus,
  }));

  const [list, setList] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [isMore, setIsMore] = useState(false); // 当前请求是否为"加载更多"
  const [hasMore, setHasMore] = useState(false);
  const [err, setErr] = useState(false);

  const requestId = useRef(0);
  const abortRef = useRef(null);
  const loadingRef = useRef(false);
  const valuesRef = useRef(normalize(initial));
  const pageRef = useRef(1);
  const failedPageRef = useRef(0);
  const enterRef = useRef(onEnterDetail);
  enterRef.current = onEnterDetail;

  const fetchPage = useCallback(async (page, values = valuesRef.current) => {
    abortRef.current?.abort(); // 取消还在飞的旧请求，不占弱网带宽
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    const id = ++requestId.current; // 序号保护：旧响应后到也会被丢弃
    valuesRef.current = values;
    loadingRef.current = true;
    setLoading(true);
    setIsMore(page > 1);
    setErr(false);
    try {
      const res = await getRentCollectionList(
        {
          roomNumber: values.roomNumber,
          contractStatus: values.contractStatus,
          overdueStatus: "all",
          page,
          pageSize: PAGE_SIZE,
        },
        { signal: ctrl.signal },
      );
      if (id !== requestId.current) return;
      if (res.data.code !== 0) throw new Error("code " + res.data.code);
      const { list: rows = [], total: count = 0 } = res.data.data || {};
      pageRef.current = page;
      failedPageRef.current = 0;
      setList((prev) => (page === 1 ? rows : mergeUnique(prev, rows)));
      setTotal(count);
      setHasMore(rows.length > 0 && page * PAGE_SIZE < count);
    } catch (e) {
      // 被 abort 的请求 id 已过期，这里自然被忽略
      if (id === requestId.current) {
        failedPageRef.current = page;
        if (page === 1) setList([]);
        setErr(true);
        message.error({ content: "加载失败，请重试", key: "rc-load-fail" }); // 同 key 只留一条
      }
    } finally {
      if (id === requestId.current) {
        loadingRef.current = false;
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    fetchPage(1);
  }, [fetchPage]);
  useEffect(
    () => () => {
      requestId.current++;
      abortRef.current?.abort();
    },
    [],
  );

  // ── 筛选 ──
  const commit = useCallback(() => {
    const values = normalize(form.getFieldsValue());
    const prev = valuesRef.current;
    if (values.roomNumber && values.roomNumber.length < MIN_ROOM_LEN)
      values.roomNumber = prev.roomNumber; // 过短沿用上次
    if (
      failedPageRef.current !== 1 &&
      values.roomNumber === prev.roomNumber &&
      values.contractStatus === prev.contractStatus
    )
      return;
    scrollToTop();
    fetchPage(1, values);
  }, [form, fetchPage, scrollToTop]);

  const debouncedCommit = useMemo(() => debounce(commit, 300), [commit]);
  useEffect(() => () => debouncedCommit.cancel(), [debouncedCommit]);
  const commitNow = useCallback(() => {
    debouncedCommit.cancel();
    commit();
  }, [debouncedCommit, commit]);
  const onValuesChange = useCallback(
    (changed) => ("roomNumber" in changed ? debouncedCommit() : commitNow()),
    [debouncedCommit, commitNow],
  );

  // ── 自动加载：哨兵进入视口下方 300px 内触发 ──
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || !hasMore || loading || err) return;
    const root = getScrollParent(el);
    const io = new IntersectionObserver(
      ([e]) =>
        e.isIntersecting &&
        !loadingRef.current &&
        fetchPage(pageRef.current + 1),
      { root: root === window ? null : root, rootMargin: "0px 0px 300px 0px" },
    );
    io.observe(el);
    return () => io.disconnect(); // 每次加载后重建，保证内容不满一屏时能继续加载
  }, [hasMore, loading, err, list.length, fetchPage]);

  const retry = () => fetchPage(failedPageRef.current || 1);
  const handleEnter = useCallback(
    (record) => enterRef.current?.(record, form.getFieldsValue()),
    [form],
  );

  return (
    <div className="rc-root">
      <style>{CSS}</style>

      <div ref={headerRef} className="rc-header">
        <div className="rc-inner">
          <div className="rc-title-row">
            <Title level={4} style={{ margin: 0, color: "#1f2329" }}>
              房屋租赁合约
            </Title>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              style={{ borderRadius: 8 }}
              onClick={() => showContractModal?.(null)}
            >
              新增
            </Button>
          </div>
          <Form
            form={form}
            className="rc-filter-row"
            initialValues={initial}
            onValuesChange={onValuesChange}
          >
            <Form.Item name="roomNumber" noStyle>
              <Input
                className="rc-search"
                allowClear
                placeholder="房间号"
                inputMode="numeric"
                enterKeyHint="search"
                onPressEnter={commitNow}
                onBlur={commitNow}
              />
            </Form.Item>
            <Form.Item name="contractStatus" noStyle>
              <Segmented
                options={[
                  { label: "关注", value: "focus" },
                  { label: "结算", value: "settlement" },
                  { label: "无效", value: "invalid" },
                ]}
              />
            </Form.Item>
          </Form>
        </div>
      </div>

      <div
        className="rc-content"
        style={{ opacity: loading && !isMore ? 0.55 : 1 }}
      >
        {loading && list.length === 0 && (
          <div style={{ textAlign: "center", padding: 48 }}>
            <Spin />
          </div>
        )}
        {!loading && !err && list.length === 0 && (
          <div style={{ textAlign: "center", color: "#94a3b8", padding: 48 }}>
            暂无合约
          </div>
        )}
        <div className="rc-grid">
          {list.map((r) => (
            <RentCard key={keyOf(r)} record={r} onClick={handleEnter} />
          ))}
        </div>
        {(list.length > 0 || err) && (
          <div ref={sentinelRef} className="rc-footer">
            {err ? (
              <Button type="link" onClick={retry}>
                加载失败，点击重试
              </Button>
            ) : loading && isMore ? (
              <Spin size="small" />
            ) : !hasMore ? (
              <Text type="secondary" style={{ fontSize: 12 }}>
                已加载全部 · 共 {total} 条
              </Text>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
}
